import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  FileText,
  Trash2,
  Plus,
  Download,
  Send,
  ExternalLink,
  CheckCircle2,
  Clock,
  Copy,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { AdminAuth, AdminShell, adminHeaders } from "@/components/admin/AdminShell";
import ContractDialog from "@/components/admin/ContractDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PUBLIC_BASE = window.location.origin;

export default function AdminContracts() {
  return (
    <AdminAuth>
      <Inner />
    </AdminAuth>
  );
}

function Inner() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/contracts`, { headers: adminHeaders() });
      setItems(res.data || []);
    } catch {
      toast.error("Failed to load contracts.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const onDelete = async (id) => {
    if (!confirm("Delete this contract? This cannot be undone.")) return;
    try {
      await axios.delete(`${API}/contracts/${id}`, { headers: adminHeaders() });
      setItems((arr) => arr.filter((c) => c.id !== id));
      toast.success("Contract deleted.");
    } catch {
      toast.error("Could not delete.");
    }
  };

  const onResend = async (id) => {
    try {
      await axios.post(`${API}/contracts/${id}/resend`, {}, { headers: adminHeaders() });
      toast.success("Resent to client.");
    } catch {
      toast.error("Could not resend.");
    }
  };

  const onDownload = async (id) => {
    try {
      const res = await axios.get(`${API}/contracts/${id}/pdf`, {
        headers: adminHeaders(),
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `OneSound-Agreement-${id.slice(0, 8)}.pdf`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch {
      toast.error("Could not download PDF.");
    }
  };

  const copyLink = (token) => {
    const url = `${PUBLIC_BASE}/sign/${token}`;
    navigator.clipboard.writeText(url);
    toast.success("Signing link copied.");
  };

  const totalSent = items.length;
  const totalSigned = items.filter((c) => c.status === "signed").length;
  const totalValue = items.reduce((s, c) => s + (Number(c.total_fee) || 0), 0);

  return (
    <AdminShell onRefresh={load} refreshing={loading}>
      <div data-testid="admin-contracts-page">
        <div className="flex items-center justify-between flex-wrap gap-4 mb-10">
          <div>
            <p className="font-body text-[11px] uppercase tracking-[0.22em] text-white/50">
              Agreements
            </p>
            <h1 className="mt-2 font-display font-black text-4xl tracking-tight text-white">
              Contracts
            </h1>
            <p className="mt-2 font-body text-white/60 text-sm max-w-xl">
              Create production agreements, email them to clients, and track
              signatures.
            </p>
          </div>
          <Button
            type="button"
            onClick={() => setOpen(true)}
            data-testid="admin-add-contract"
            className="rounded-full h-12 px-6 bg-white text-black hover:bg-brand-magenta hover:text-white font-display font-bold"
          >
            <Plus size={16} className="mr-2" /> New Contract
          </Button>
        </div>

        <div className="grid sm:grid-cols-3 gap-4 mb-10">
          <Stat label="Total Sent" value={totalSent} />
          <Stat label="Signed" value={totalSigned} accent="text-emerald-400" />
          <Stat
            label="Total Value"
            value={`$${totalValue.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`}
            accent="text-brand-magenta"
          />
        </div>

        {items.length === 0 ? (
          <div className="rounded-3xl border border-white/10 bg-white/[0.02] p-16 text-center">
            <FileText size={32} className="mx-auto text-white/30" />
            <p className="mt-4 font-display font-bold text-xl text-white">
              No contracts yet.
            </p>
            <p className="mt-2 font-body text-white/50 text-sm">
              Click &ldquo;New Contract&rdquo; to draft one for a client.
            </p>
          </div>
        ) : (
          <ul className="space-y-4">
            {items.map((c) => (
              <li
                key={c.id}
                className="rounded-2xl border border-white/10 bg-white/[0.02] p-5 lg:p-6 hover:bg-white/[0.04] transition-colors"
              >
                <div className="flex items-start justify-between gap-6 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-3 flex-wrap">
                      <h3 className="font-display font-extrabold text-lg text-white">
                        {c.client_organization || c.client_name}
                      </h3>
                      <TypePill type={c.contract_type} />
                      <StatusPill status={c.status} signedAt={c.client_signed_at} />
                      <span className="font-body text-xs text-white/40">
                        {new Date(c.created_at).toLocaleDateString()}
                      </span>
                    </div>
                    <p className="mt-1 font-body text-sm text-white/60">
                      {c.event_name || "—"}{" "}
                      {c.event_dates ? `· ${c.event_dates}` : ""}
                    </p>
                    <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-xs text-white/50 font-body">
                      <span>To: {c.client_email}</span>
                      {c.total_fee != null && (
                        <span>
                          Fee:{" "}
                          <strong className="text-white/80">
                            ${Number(c.total_fee).toLocaleString()}
                          </strong>
                        </span>
                      )}
                      {c.deposit != null && (
                        <span>
                          Deposit:{" "}
                          <strong className="text-white/80">
                            ${Number(c.deposit).toLocaleString()}
                          </strong>
                        </span>
                      )}
                      {c.selected_services?.length > 0 && (
                        <span>{c.selected_services.join(", ")}</span>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-2 shrink-0">
                    <a
                      href={`/sign/${c.sign_token}`}
                      target="_blank"
                      rel="noreferrer"
                      title="Open client signing view"
                      className="h-9 w-9 rounded-full border border-white/10 bg-white/5 text-white/70 hover:text-white hover:bg-white/10 flex items-center justify-center"
                    >
                      <ExternalLink size={14} />
                    </a>
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={() => copyLink(c.sign_token)}
                      title="Copy signing link"
                      className="text-white/70 hover:text-white hover:bg-white/10 rounded-full h-9 w-9 p-0"
                    >
                      <Copy size={14} />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={() => onResend(c.id)}
                      title="Resend email"
                      className="text-white/70 hover:text-white hover:bg-white/10 rounded-full h-9 w-9 p-0"
                    >
                      <Send size={14} />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={() => onDownload(c.id)}
                      title="Download PDF"
                      className="text-white/70 hover:text-white hover:bg-white/10 rounded-full h-9 w-9 p-0"
                    >
                      <Download size={14} />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={() => onDelete(c.id)}
                      title="Delete"
                      className="text-white/40 hover:text-red-400 hover:bg-red-500/10 rounded-full h-9 w-9 p-0"
                    >
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <ContractDialog
        open={open}
        onOpenChange={setOpen}
        onCreated={(c) => setItems((arr) => [c, ...arr])}
      />
    </AdminShell>
  );
}

function Stat({ label, value, accent = "text-white" }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6">
      <p className="font-body text-[11px] uppercase tracking-[0.22em] text-white/50">
        {label}
      </p>
      <p className={`mt-3 font-display font-black text-4xl tracking-tight ${accent}`}>
        {value}
      </p>
    </div>
  );
}

function TypePill({ type }) {
  const isMusician = type === "musician_single_service";
  const label = isMusician ? "Musician — Single Service" : "Production";
  const cls = isMusician
    ? "bg-fuchsia-500/15 text-fuchsia-300 border-fuchsia-500/30"
    : "bg-white/10 text-white/80 border-white/20";
  return (
    <span
      data-testid={`contract-type-pill-${isMusician ? "musician" : "production"}`}
      className={`inline-flex items-center px-3 py-1 rounded-full text-[10px] uppercase tracking-[0.2em] border ${cls}`}
    >
      {label}
    </span>
  );
}

function StatusPill({ status, signedAt }) {
  if (status === "signed" || signedAt) {
    return (
      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] uppercase tracking-[0.2em] bg-emerald-500/15 text-emerald-400 border border-emerald-500/30">
        <CheckCircle2 size={11} /> Signed
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] uppercase tracking-[0.2em] bg-amber-500/15 text-amber-400 border border-amber-500/30">
      <Clock size={11} /> Awaiting
    </span>
  );
}
